import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ChevronDown } from 'lucide-react';
const SelectContext = React.createContext(undefined);
function useSelectContext() {
    const context = React.useContext(SelectContext);
    if (!context) {
        throw new Error('Select components must be used within Select');
    }
    return context;
}
export function Select({ value: controlledValue, defaultValue = '', onValueChange, children }) {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [open, setOpen] = React.useState(false);
    const value = controlledValue !== undefined ? controlledValue : internalValue;
    const handleValueChange = (newValue) => {
        if (controlledValue === undefined) {
            setInternalValue(newValue);
        }
        onValueChange?.(newValue);
        setOpen(false);
    };
    return (_jsx(SelectContext.Provider, { value: { value, onValueChange: handleValueChange, open, setOpen }, children: _jsx("div", { className: "relative", children: children }) }));
}
export function SelectTrigger({ children, className = '' }) {
    const { open, setOpen } = useSelectContext();
    return (_jsxs("button", { type: "button", onClick: () => setOpen(!open), className: `flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`, children: [children, _jsx(ChevronDown, { className: `h-4 w-4 opacity-50 transition-transform ${open ? 'rotate-180' : ''}` })] }));
}
export function SelectValue({ placeholder }) {
    const { value } = useSelectContext();
    const content = React.useContext(SelectContentContext);
    if (!value) {
        return _jsx("span", { className: "text-gray-500", children: placeholder || 'Select...' });
    }
    // Find the selected item's label
    const selectedItem = content?.items.find((item) => item.value === value);
    return _jsx("span", { children: selectedItem?.children || value });
}
const SelectContentContext = React.createContext(undefined);
export function SelectContent({ children, className = '' }) {
    const { open, setOpen } = useSelectContext();
    const [items, setItems] = React.useState([]);
    // Extract items from children - do this always, not just when open
    React.useEffect(() => {
        const newItems = [];
        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && typeof child.props === 'object' && child.props !== null) {
                const childProps = child.props;
                if (childProps.value) {
                    newItems.push({
                        value: childProps.value,
                        children: childProps.children,
                    });
                }
            }
        });
        setItems(newItems);
    }, [children]);
    React.useEffect(() => {
        if (!open)
            return;
        const handleClickOutside = () => setOpen(false);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [open, setOpen]);
    return (_jsx(SelectContentContext.Provider, { value: { items }, children: open && (_jsx("div", { className: `absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg ${className}`, onClick: (e) => e.stopPropagation(), children: children })) }));
}
export function SelectItem({ value, children, className = '' }) {
    const { value: selectedValue, onValueChange } = useSelectContext();
    const isSelected = selectedValue === value;
    return (_jsx("div", { onClick: () => onValueChange(value), className: `cursor-pointer px-3 py-2 text-sm hover:bg-gray-100 ${isSelected ? 'bg-gray-100 font-medium' : ''} ${className}`, children: children }));
}
